import { OrderState } from "@app/store/order.slice";
import {
  Address,
  CustomFieldItem,
  OrderCustomFieldDifinitions,
  OrderCustomFields,
  OrderDeliveryAddress,
  OrderDropoffAddress,
  OrderPickupAddress,
} from "@app/types/orders";
import { Languages } from "@app/types/multilingual";
import { t } from "@app/utils/translation";

/**
 * Resolves raw custom-field values/definitions into the legacy
 * `CustomFieldItem[]` shape. Mirrors the (3x-duplicated) inline logic in
 * `order.slice.ts`'s `fetchTrackOrder.fulfilled` exactly — kept as a single
 * implementation here so the socket path doesn't drift from it.
 */
export function resolveAddressCustomFields(
  rawCustomFields: OrderCustomFields | null | undefined,
  rawCustomFieldDefinitions: OrderCustomFieldDifinitions[] | null | undefined
): CustomFieldItem[] | null {
  if (!rawCustomFieldDefinitions || rawCustomFieldDefinitions.length === 0 || !rawCustomFields) {
    return null;
  }

  const customFields: CustomFieldItem[] = [];

  rawCustomFieldDefinitions.forEach((cfd) => {
    const rawValue = rawCustomFields[cfd.key];
    if (rawValue === undefined || rawValue === "" || rawValue === null) return;

    let value = rawValue;
    let label = cfd.label;

    if (cfd.config.type === "select") {
      value = cfd.config.choices.find((c) => c.key === rawValue)?.label ?? "-";
    } else if (cfd.config.type === "file") {
      const data: any = rawValue;
      label = data.name;
      value = data.url;
    } else if (cfd.config.type === "boolean" && rawValue === true) {
      value = rawValue;
    }

    customFields.push({ key: cfd.key, label, value, type: cfd.config.type });
  });

  return customFields.length > 0 ? customFields : null;
}

/**
 * Mirrors the addressDetail derivation in `order.slice.ts`: PickupDelivery
 * orders carry a ready-made `addressDetail` string; every other order type
 * builds it from block/floor/room. Accepts the loosely-typed subset both
 * `OrderPickupAddress` and `OrderDropoffAddress`/`OrderDeliveryAddress`
 * satisfy structurally, so one function covers pickup and dropoff.
 */
export function computeAddressDetail(
  entity: { addressDetail?: string; buildingBlock?: string; floor?: string; room?: string },
  orderType: OrderState["type"],
  language: Languages
): string {
  if (orderType === "PickupDelivery" && entity.addressDetail !== undefined) {
    return entity.addressDetail;
  }

  const block = entity.buildingBlock ? `${t("Block", language)} ${entity.buildingBlock}, ` : "";
  const floor = entity.floor ? `${t("Floor", language)} ${entity.floor}, ` : "";
  const room = entity.room ? `${t("Room", language)} ${entity.room}` : "";
  return block + floor + room;
}

/** Builds the full `Address` for a pickup from a fresh socket payload,
 * preserving any existing field the payload has no equivalent for. */
export function mapSocketPickup(
  pickup: OrderPickupAddress,
  existing: Address,
  orderType: OrderState["type"],
  language: Languages
): Address {
  return {
    ...existing,
    id: pickup.id || existing.id,
    lat: pickup.coordinates[1],
    lng: pickup.coordinates[0],
    address: pickup.address,
    addressDetail: computeAddressDetail(pickup, orderType, language),
    customFields: resolveAddressCustomFields(pickup.customFields, pickup.customFieldDefinitions),
    deliveryDate: pickup.deliveryDate,
    proofs: pickup.proofs,
    customerDescription: pickup.customerDescription,
    proofNote: pickup.popNote || pickup.driverDescription,
    rawCustomFields: pickup.customFields,
    rawCustomFieldDefinitions: pickup.customFieldDefinitions,
    contactName: pickup.fullName,
    contactPhone: pickup.phone,
  };
}

/** Builds the full `Address` for a dropoff/delivery from a fresh socket
 * payload, preserving any existing field the payload has no equivalent for.
 * `drop` covers both `OrderDropoffAddress` (Ondemand/Delivery) and
 * `OrderDeliveryAddress` (PickupDelivery) — same union `order.slice.ts`
 * merges on the REST path. */
export function mapSocketDropoff(
  drop: OrderDropoffAddress | OrderDeliveryAddress,
  existing: Address,
  orderType: OrderState["type"],
  language: Languages
): Address {
  return {
    ...existing,
    id: drop.id || existing.id,
    lat: drop.coordinates[1],
    lng: drop.coordinates[0],
    address: drop.address,
    addressDetail: computeAddressDetail(drop, orderType, language),
    customFields: resolveAddressCustomFields(drop.customFields, drop.customFieldDefinitions),
    deliveryDate: drop.deliveryDate,
    proofs: drop.proofs,
    customerDescription: "customerDescription" in drop ? drop.customerDescription : "",
    proofNote: drop.podNote || ("driverDescription" in drop ? drop.driverDescription : ""),
    rawCustomFields: drop.customFields,
    rawCustomFieldDefinitions: drop.customFieldDefinitions,
    status: drop.status,
    contactName: "fullName" in drop ? drop.fullName : drop.fullname,
    contactPhone: drop.phone,
  };
}

export type DropoffQueueState = {
  remainingQueuePosition: number;
  isLastDropOff: boolean;
};

/**
 * Client-side port of the server's Track Drop-off API calculation, needed
 * because `customer:order:updated` socket payloads carry the raw
 * `dropoffs[]` array but never a pre-computed `remainingQueuePosition`/
 * `isLastDropOff` for the specific dropoff being tracked — unlike the REST
 * `fetchTrackOrder` response, which returns those two already resolved.
 * Same logic as the server (which finds the tracked dropoff by
 * `trackOrderCode`), except identified by `id` instead: `Address.id` (the
 * already-loaded tracked dropoff's id in Redux) and the raw socket
 * dropoff's own `id` are the same stable, server-assigned identifier —
 * reusing it avoids threading a second, redundant identifier through the
 * socket payload/Redux just for this lookup.
 * Returns `null` (rather than throwing) when the tracked dropoff isn't in
 * `dropoffs` — the caller should simply skip updating those two fields for
 * this socket message.
 */
export function computeDropoffQueueState(
  dropoffs: OrderDropoffAddress[] | undefined,
  trackedDropoffId: string | undefined
): DropoffQueueState | null {
  if (!dropoffs || dropoffs.length === 0 || !trackedDropoffId) return null;

  const dropoffIndex = dropoffs.findIndex((dropoff) => dropoff.id === trackedDropoffId);
  if (dropoffIndex < 0) return null;

  const remainingQueuePosition = dropoffs
    .slice(0, dropoffIndex)
    .filter((dropoff) => dropoff.status !== "Done").length;

  const isLastDropOff = dropoffIndex === dropoffs.length - 1;

  return { remainingQueuePosition, isLastDropOff };
}
